import { ArrowRight } from 'lucide-react';

import {
  CtaArrow,
  Band,
  ctaClasses,
  Medallion,
  panelClasses,
  Pill,
  SectionTitle,
} from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero, Reveal } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { COMPARISONS, COMPARISON_ORDER } from '@/features/comparisons';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { CATEGORY_MARK } from './category-mark';

import type { ReactElement } from 'react';

/**
 * The bedrock card, spread onto a router `<Link>` — `LinkPanel` would work but
 * takes a plain href, and these destinations need the locale-aware
 * `LocalizedLink`.
 */
const CARD_CLASS = panelClasses({
  interactive: true,
  className: 'flex h-full flex-col gap-4 p-6',
});

/**
 * AF-136 — `/vs` index.
 *
 * ## What the card says now, and why it changed
 *
 * Eight comparisons rendered as eight visually identical cards: white ground,
 * one bold line, one grey paragraph. Nothing distinguished a head-on competitor
 * from a product that is not a competitor at all, and the paragraph's most
 * useful half — the verdict — was its last sentence, which is the one place a
 * scanning reader never reaches.
 *
 * Three changes, all of them information rather than decoration:
 *
 *  - **A category, with an icon and a hue.** Two of the eight (Glean,
 *    CopilotKit) are adjacent rather than alternatives, and the page knew that
 *    and did not say it above the fold.
 *  - **The verdict on its own line**, under a label, in the ink the rest of the
 *    site uses for a link — so "where Syncanix fits" is the thing the eye lands
 *    on rather than the thing it gives up before.
 *  - **An explicit affordance.** The whole card was already the link (the
 *    `ui-design.md` § 17 simple shape); nothing said so.
 *
 * The summary sentence these two halves come from is unchanged and still
 * carries the page's meta description — `comparison-content.test.ts` pins that
 * the halves concatenate back to it, in every locale.
 */
export function VsIndex(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('vs.index.metaTitle'),
    description: t('vs.index.metaDescription'),
    path: '/vs',
  });
  return (
    <main
      id="main"
      tabIndex={-1}
      className="min-h-screen bg-[color:var(--bd-page)] text-base leading-relaxed"
    >
      <PageHero title={t('vs.index.title')} description={t('vs.index.intro')} />

      <Container width="default" className="py-14 sm:py-16">
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {COMPARISON_ORDER.map((slug, i) => {
            const data = COMPARISONS[slug];
            const mark = CATEGORY_MARK[data.category];
            return (
              <Reveal as="li" key={slug} delayMs={Math.min(i, 4) * 60}>
                <Link to={`/vs/${slug}`} className={CARD_CLASS}>
                  <div className="flex items-center gap-3">
                    <Medallion icon={mark.icon} hue={mark.hue} size="sm" />
                    <Pill>{t(`vs.category.${data.category}`)}</Pill>
                  </div>
                  <h2 className="text-[1.12rem] font-semibold tracking-[-0.026em] text-[color:var(--bd-ink)]">
                    {t('vs.index.cardHeading', { rival: data.rivalName })}
                  </h2>
                  <p className="text-pretty text-[0.9rem] leading-[1.6] text-[color:var(--bd-muted)]">
                    {t(data.theirsKey)}
                  </p>
                  {/* The verdict, set apart. `mt-auto` pins it to the bottom of
                      whichever card in the row is tallest, so the eight verdicts
                      line up across the grid instead of floating at eight
                      different heights. */}
                  <div className="mt-auto border-t border-[color:var(--bd-line)] pt-4">
                    <p className="font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.14em] text-[color:var(--bd-subtle)] rtl:normal-case rtl:tracking-normal">
                      {t('vs.index.verdict')}
                    </p>
                    <p className="mt-2 text-pretty text-[0.9rem] leading-[1.6] text-[color:var(--bd-ink)]">
                      {t(data.verdictKey)}
                    </p>
                    <span
                      aria-hidden="true"
                      className="mt-3 inline-flex items-center gap-1.5 text-[0.82rem] font-medium text-[color:var(--bd-link)]"
                    >
                      {t('vs.index.readMore')}
                      <ArrowRight className="size-3.5 rtl:rotate-180" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </ul>
      </Container>

      {/* The page ended on the eighth card and then the footer. A comparison
          index is read by someone actively choosing, which makes it the highest-
          intent page on the site and the worst one to leave without a next
          step — and the sibling indexes (`/features`, `/one-pagers`) both close
          on this band, so ending here was also the odd one out. */}
      <Band
        as="section"
        tone="band"
        space="sm"
        className="text-center"
        aria-labelledby="vs-closing"
      >
        <SectionTitle
          onDark
          id="vs-closing"
          className="mx-auto max-w-2xl text-[clamp(1.4rem,2.6vw,1.95rem)]"
        >
          {t('vs.index.closingHeading')}
        </SectionTitle>
        <p className="mx-auto mt-3 max-w-xl text-pretty text-[0.95rem] leading-[1.66] text-[color:var(--bd-band-body)]">
          {t('vs.index.closingBody')}
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/contact" className={ctaClasses({ onDark: true })}>
            {t('vs.index.closingCta')}
            <CtaArrow />
          </Link>
          <Link to="/benchmarks" className={ctaClasses({ variant: 'secondary', onDark: true })}>
            {t('vs.index.closingSecondary')}
          </Link>
        </div>
      </Band>
    </main>
  );
}
